/**
 * Commercial line tax — single entry for sales/purchases line breakdown.
 * Sale amounts use product price_mode (default final). Does not invent rates.
 */

import type { PriceMode, TaxCategory, TaxCalculateResult } from "./types.js";
import { calculateTax, aggregateTaxLines, roundMoney } from "./index.js";

export type FiscalBasis = {
  priceMode?: PriceMode | null;
  taxCategory?: TaxCategory | null;
  /** Percent; null = unknown (do not invent 21). */
  taxRate?: number | null;
};

export type LineTaxBreakdown = TaxCalculateResult & {
  rateKnown: boolean;
  note?: string;
};

export function resolveFiscal(basis?: FiscalBasis | null): {
  priceMode: PriceMode;
  taxCategory: TaxCategory;
  taxRate: number;
  rateKnown: boolean;
  note?: string;
} {
  const priceMode: PriceMode = basis?.priceMode === "net" ? "net" : "final";
  const taxCategory = (basis?.taxCategory ?? "taxable") as TaxCategory;
  const rateKnown = taxCategory !== "taxable" || (basis?.taxRate != null && Number.isFinite(basis.taxRate));
  const taxRate =
    taxCategory !== "taxable" ? 0 : rateKnown ? Number(basis!.taxRate) : 0;
  return {
    priceMode,
    taxCategory,
    taxRate,
    rateKnown,
    note: !rateKnown
      ? "Sin alícuota en el producto: el desglose de IVA no está determinado (no se inventa 21%)."
      : undefined,
  };
}

/** Unit sale/purchase amount → full line breakdown. */
export function taxLineFromAmount(
  unitAmount: number,
  quantity: number,
  basis?: FiscalBasis | null,
): LineTaxBreakdown {
  const f = resolveFiscal(basis);
  const r = calculateTax({
    amount: unitAmount,
    quantity,
    priceMode: f.priceMode,
    taxCategory: f.taxCategory,
    taxRate: f.taxRate,
  });
  return { ...r, rateKnown: f.rateKnown, note: f.note };
}

export function aggregateLineBreakdowns(lines: LineTaxBreakdown[]): {
  netAmount: number;
  taxAmount: number;
  grossAmount: number;
  allRatesKnown: boolean;
} {
  const agg = aggregateTaxLines(lines);
  return {
    ...agg,
    allRatesKnown: lines.every((l) => l.rateKnown),
  };
}

/** Persistable snapshot (JSONB-friendly). */
export function toTaxSnapshot(b: LineTaxBreakdown): Record<string, unknown> {
  return {
    priceMode: b.priceMode,
    taxCategory: b.taxCategory,
    taxRate: b.taxRate,
    unitNet: b.unitNet,
    unitTax: b.unitTax,
    unitGross: b.unitGross,
    netAmount: b.netAmount,
    taxAmount: b.taxAmount,
    grossAmount: b.grossAmount,
    quantity: b.quantity,
    rateKnown: b.rateKnown,
    note: b.note ?? null,
  };
}

export { roundMoney };
